/**
 * @fileoverview Input/output schemas for the multi-reference-image-creator-fal-v2 agent.
 *
 * This agent generates images with multiple character references using FLUX 2 Pro Edit.
 * Accepts character reference images with indexed mappings for visual consistency.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema.extend({
  /** The prompt with numbered image references (e.g., "the woman from image 1...") */
  imagePrompt: z.string().describe('The prompt using numbered image references.'),
  /** Array of character reference images with their mapping information */
  referenceImages: z.array(z.object({
    characterName: z.string(),
    imageIndex: z.number().min(1),
    imageDataUri: z.string(),
    characterId: z.string().optional(),
  })).min(1).describe('Character images with their index mapping.'),
  /** Override the default model */
  overrideModel: z.string().optional(),
  /** Normalized aspect ratio */
  aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional(),
  /** Quality level — maps to backend-specific resolution (e.g. xAI 1k/2k). */
  quality: z.enum(['draft', 'standard', 'high']).optional(),
  /** Enable/disable safety checker */
  safetyChecker: z.boolean().optional(),
  /** Output format */
  outputFormat: z.enum(['png', 'jpeg', 'webp']).optional(),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  imageDataUri: z.string().describe('The URL of the generated image.'),
  referenceMapping: z.array(z.object({
    characterName: z.string(),
    imageIndex: z.number(),
  })).describe('The character-to-image mapping used for this generation.'),
  seed: z.number().optional().describe('The seed used for this generation, if returned by the model.'),
  generatingModel: z.string().optional().describe('The FAL model identifier used for generation.'),
  aspectRatio: z.enum(['square', 'portrait', 'landscape', 'wide', 'tall']).optional().describe('The normalized aspect ratio used.'),
  safetyChecker: z.boolean().optional().describe('Whether safety checker was enabled.'),
  timings: z.object({ inference: z.number().optional(), total: z.number().optional() }).optional().describe('Generation timing data in ms.'),
});

export type Output = z.infer<typeof OutputSchema>;
